import {Component, OnInit} from '@angular/core';
import {NavigationEnd, Router, RouterModule, Event as RouterEventType} from '@angular/router';
import {AskAiComponent} from './ask-ai/ask-ai.component';
import {NzModalModule} from 'ng-zorro-antd/modal';
import {filter} from 'rxjs';
import {CommonModule} from '@angular/common';
import {
  LANDING_PAGE_CONTEXT_COOKIE_KEY,
  LANDING_PAGE_CONTEXT_STORAGE_KEY,
  LANDING_PAGE_START_URL_COOKIE_KEY,
  LANDING_PAGE_START_URL_STORAGE_KEY
} from './constants/app.constants';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterModule,
    AskAiComponent,
    NzModalModule,
    CommonModule
  ],
  template: `
    <router-outlet></router-outlet>
    <app-ask-ai *ngIf="!isLandingPage" />
  `
})
export class AppComponent implements OnInit{
  isLandingPage: boolean = typeof window !== 'undefined' && this.isFocusedLandingPath(window.location.pathname);
  constructor(private router: Router) {
  }
  ngOnInit(): void {
    this.handleStandaloneLaunchRedirect();
    this.router.events.pipe(
      filter((event: RouterEventType) => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.isLandingPage = this.isFocusedLandingPath(event.urlAfterRedirects ?? event.url);
      this.storeDefaultStartUrlForMainSite(event);
    });
  }

  private isFocusedLandingPath(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path.startsWith('/landing-page') || path === '/kostenlos-starten' || path === '/start-free';
  }

  private handleStandaloneLaunchRedirect(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const {isStandaloneDisplayMode, isIosStandalone, isAnyStandalone} = this.getStandaloneState();

    // console.log('[PWA Redirect] Launch context detected', {
    //   href: window.location.href,
    //   pathname: window.location.pathname,
    //   isStandaloneDisplayMode,
    //   isIosStandalone,
    // });

    if (!isAnyStandalone) {
      // console.log('[PWA Redirect] Not running in standalone display mode – skipping redirect logic.');
      return;
    }

    if (!isIosStandalone) {
      // console.log('[PWA Redirect] Standalone redirect only applies to iOS – skipping for current platform.');
      return;
    }

    let storedUrl: string | null = null;
    try {
      storedUrl = window.localStorage.getItem(LANDING_PAGE_START_URL_STORAGE_KEY);
      // console.log('[PWA Redirect] Retrieved stored landing page URL from localStorage', {
      //   storageKey: LANDING_PAGE_START_URL_STORAGE_KEY,
      //   storedUrl,
      // });
    } catch (error) {
      console.warn('[PWA Redirect] Failed to read stored landing page URL from localStorage', error);
      storedUrl = null;
    }

    if (!storedUrl) {
      storedUrl = this.readCookie(LANDING_PAGE_START_URL_COOKIE_KEY);
      if (storedUrl) {
        // console.log('[PWA Redirect] Retrieved stored landing page URL from cookies', {
        //   cookie: LANDING_PAGE_START_URL_COOKIE_KEY,
        //   storedUrl,
        // });
      }
    }

    if (!storedUrl) {
      // console.log('[PWA Redirect] No stored landing page URL found – skipping redirect.');
      return;
    }

    let storedContext: string | null = null;
    try {
      storedContext = window.localStorage.getItem(LANDING_PAGE_CONTEXT_STORAGE_KEY);
      // console.log('[PWA Redirect] Retrieved landing page context flag from localStorage', {
      //   storageKey: LANDING_PAGE_CONTEXT_STORAGE_KEY,
      //   storedContext,
      // });
    } catch (error) {
      console.warn('[PWA Redirect] Failed to read landing page context flag from localStorage', error);
      storedContext = null;
    }

    if (!storedContext) {
      storedContext = this.readCookie(LANDING_PAGE_CONTEXT_COOKIE_KEY);
      if (storedContext) {
        // console.log('[PWA Redirect] Retrieved landing page context flag from cookies', {
        //   cookie: LANDING_PAGE_CONTEXT_COOKIE_KEY,
        //   storedContext,
        // });
      }
    }

    if (storedContext !== 'landing') {
      // console.log('[PWA Redirect] Landing page context flag not set – skipping redirect.', {
      //   storedContext,
      // });
      return;
    }

    let targetUrl: URL;
    try {
      targetUrl = new URL(storedUrl, window.location.origin);
      // console.log('[PWA Redirect] Parsed stored landing page URL', {
      //   targetHref: targetUrl.href,
      //   targetOrigin: targetUrl.origin,
      //   currentOrigin: window.location.origin,
      // });
    } catch (error) {
      console.warn('[PWA Redirect] Unable to parse stored landing page URL – skipping redirect.', {
        storedUrl,
        error,
      });
      return;
    }

    if (targetUrl.origin !== window.location.origin) {
      console.warn('[PWA Redirect] Stored landing page URL origin mismatches current origin – skipping redirect.', {
        targetOrigin: targetUrl.origin,
        currentOrigin: window.location.origin,
      });
      return;
    }

    const isRootLikeLaunch = this.isRootLikePath(window.location.pathname);
    if (isRootLikeLaunch && window.location.href !== targetUrl.href) {
      // console.log('[PWA Redirect] Redirecting standalone launch to stored landing page URL', {
      //   currentHref: window.location.href,
      //   targetHref: targetUrl.href,
      // });
      window.location.replace(targetUrl.href);
    } else {
      // console.log('[PWA Redirect] No redirect performed', {
      //   isRootLikeLaunch,
      //   currentHref: window.location.href,
      //   targetHref: targetUrl.href,
      // });
    }
  }

  private storeDefaultStartUrlForMainSite(event: NavigationEnd): void {
    if (typeof window === 'undefined') return;

    const urlAfterRedirects = event.urlAfterRedirects ?? event.url;
    if (urlAfterRedirects !== '/') return;

    const { isAnyStandalone } = this.getStandaloneState();
    if (isAnyStandalone) return;

    // 🔒 Do not overwrite if a landing context is already stored
    try {
      const existingCtxLocal  = window.localStorage.getItem(LANDING_PAGE_CONTEXT_STORAGE_KEY);
      const existingCtxCookie = this.readCookie(LANDING_PAGE_CONTEXT_COOKIE_KEY);
      if (existingCtxLocal === 'landing' || existingCtxCookie === 'landing') {
        return;
      }
    } catch (_) {
      // ignore
    }

    try {
      const defaultStartUrl = `${window.location.origin}/`;
      window.localStorage.setItem(LANDING_PAGE_START_URL_STORAGE_KEY, defaultStartUrl);
      window.localStorage.setItem(LANDING_PAGE_CONTEXT_STORAGE_KEY, 'root');
    } catch (error) {
      console.warn('[PWA Redirect] Unable to store default start URL in localStorage', error);
    }

    this.writeCookie(LANDING_PAGE_START_URL_COOKIE_KEY, `${window.location.origin}/`);
    this.writeCookie(LANDING_PAGE_CONTEXT_COOKIE_KEY, 'root');
  }

  private getStandaloneState(): { isStandaloneDisplayMode: boolean; isIosStandalone: boolean; isAnyStandalone: boolean } {
    const matchMedia = window.matchMedia?.bind(window) ?? (() => ({matches: false}) as MediaQueryList);
    const isStandaloneDisplayMode = matchMedia('(display-mode: standalone)').matches
      || matchMedia('(display-mode: fullscreen)').matches
      || matchMedia('(display-mode: minimal-ui)').matches;

    const navigatorAny = window.navigator as any;
    const rawIosStandalone = navigatorAny?.standalone;
    const hasIosStandaloneFlag = typeof rawIosStandalone === 'boolean' ? rawIosStandalone : !!rawIosStandalone;

    const userAgent = (navigatorAny?.userAgent ?? navigatorAny?.vendor ?? '').toLowerCase();
    const platform = (navigatorAny?.platform ?? '').toLowerCase();
    const maxTouchPoints = Number(navigatorAny?.maxTouchPoints ?? 0);
    const isTouchMac = platform === 'macintel' && maxTouchPoints > 1;
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent) || isTouchMac;

    const isIosStandalone = hasIosStandaloneFlag || (isIosDevice && isStandaloneDisplayMode);

    return {
      isStandaloneDisplayMode,
      isIosStandalone,
      isAnyStandalone: isStandaloneDisplayMode || isIosStandalone,
    };
  }

  private readCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    try {
      const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      console.warn('[PWA Redirect] Failed to read cookie value', { name, error });
      return null;
    }
  }

  private writeCookie(name: string, value: string, maxAgeSeconds = 60 * 60 * 24 * 30): void {
    if (typeof document === 'undefined') {
      return;
    }

    try {
      let cookieValue = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
      if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
        cookieValue += '; Secure';
      }
      document.cookie = cookieValue;
      // console.log('[PWA Redirect] Stored cookie value', { name, value });
    } catch (error) {
      console.warn('[PWA Redirect] Unable to store cookie value', { name, error });
    }
  }

  private isRootLikePath(pathname: string): boolean {
    return pathname === '/' || pathname === '/index.html';
  }
}
